/**
 * DVL Gallery Filter (Fashionopolism / Flickity)
 * ==============================================
 * Per-variant + "collection" image filtering for Fashionopolism's Flickity
 * galleries. Fashionopolism shows all media and only switches the active cell on
 * variant change, so this filters the Flickity cells client-side using Flickity's
 * remove()/append() API — theme.js is left untouched.
 *
 * Rules mirror the Dawn Liquid snippets:
 *   - "collection" in filename       -> always hidden (if hide_collection on)
 *   - variant-specific (SKU in name, OR the variant's featured media) -> shown
 *     only for the exact selected variant
 *   - body/non-variant image         -> matched by the selected variant's Colour
 *   - otherwise                      -> shown
 *
 * Relies on:
 *   - <script class="product-json"> : full product JSON (variants, options, featured_media)
 *   - each .carousel-cell            : data-image-id="<media.id>" + data-dvl-file="<filename>"
 *   - #product-gallery flags         : data-dvl-filter-variant / data-dvl-hide-collection
 *
 * Gated by the two flags; inert unless enabled in the section settings.
 */
(function () {
  'use strict';

  var STORE = new WeakMap(); // carousel el -> [full cell outerHTML]

  function norm(s) { return (s || '').toString().toLowerCase(); }

  function readFlags() {
    var host = document.getElementById('product-gallery') ||
               document.querySelector('[id^="product-gallery"], [data-dvl-filter-variant]');
    if (!host) return { on: false };
    var fv = host.getAttribute('data-dvl-filter-variant');
    var hc = host.getAttribute('data-dvl-hide-collection');
    return {
      host: host,
      filterVariant: fv === 'true',
      hideCollection: hc !== 'false', // default ON
      on: fv === 'true' || hc !== 'false'
    };
  }

  function getProduct() {
    var el = document.querySelector('.product-json, [id^="ProductJson-"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function colorIndex(product) {
    for (var i = 0; i < (product.options || []).length; i++) {
      var o = product.options[i];
      var name = (o && o.name) ? o.name : o; // options may be strings or {name}
      if (/^colou?r$/i.test(name)) return i;
    }
    return -1;
  }

  function buildContext(product) {
    var ci = colorIndex(product);
    var skus = product.variants.map(function (v) { return norm(v.sku); }).filter(Boolean);
    var colorValues = [];
    if (ci >= 0) {
      product.variants.forEach(function (v) {
        var c = norm(v.options && v.options[ci]);
        if (c && colorValues.indexOf(c) < 0) colorValues.push(c);
      });
    }
    var featuredOwner = {}; // media/image id -> owning variant sku
    product.variants.forEach(function (v) {
      var s = norm(v.sku);
      if (v.featured_media && v.featured_media.id) featuredOwner[v.featured_media.id] = s;
      if (v.featured_image && v.featured_image.id) featuredOwner[v.featured_image.id] = s;
    });
    return { ci: ci, skus: skus, colorValues: colorValues, featuredOwner: featuredOwner, variants: product.variants };
  }

  /* A variant with NO images of its own (e.g. a port length added before it was photographed) would
     otherwise leave the gallery empty. Don't fall back to EVERYTHING, which would show every variant's
     shots plus the hidden "collection" composites. Borrow a sibling's set instead: same colour first,
     in product order, so Black / Z Long shows the Black / Long images. */
  function standIns(ctx, variant) {
    var colour = function (v) { return ctx.ci >= 0 ? norm(v.options && v.options[ctx.ci]) : ''; };
    var mine = colour(variant);
    var others = (ctx.variants || []).filter(function (v) { return v.id !== variant.id; });
    return others.filter(function (v) { return colour(v) === mine; })
      .concat(others.filter(function (v) { return colour(v) !== mine; }));
  }

  function isCollectionFile(file) { return norm(file).indexOf('collection') >= 0; }

  function currentVariant(product) {
    // The FORM is the live selection; `?variant=` is only how the page was entered. Reading the URL
    // first pinned the gallery to the entry variant on any theme that updates the selectors without
    // rewriting the URL — pick another option and the images never changed again.
    var input = document.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
    var vid = input && input.value ? input.value : null;
    if (!vid) vid = new URLSearchParams(location.search).get('variant');
    var v = null;
    if (vid) v = product.variants.filter(function (x) { return String(x.id) === String(vid); })[0];
    if (!v) v = product.variants.filter(function (x) { return x.available; })[0] || product.variants[0];
    return v;
  }

  function ownerOf(file, cellId, ctx) {
    for (var i = 0; i < ctx.skus.length; i++) {
      var s = ctx.skus[i];
      if (file.indexOf(s + '-') >= 0 || file.indexOf(s + '.') >= 0 || file.indexOf(s + '_') >= 0) return s;
    }
    if (cellId && ctx.featuredOwner[cellId]) return ctx.featuredOwner[cellId];
    return '';
  }

  function imgColorOf(file, ctx) {
    var found = '';
    ctx.colorValues.forEach(function (c) { if (c && file.indexOf(c) >= 0) found = c; });
    return found;
  }

  function cellVisible(cell, ctx, variant, flags) {
    var file = norm(cell.getAttribute('data-dvl-file'));
    var cellId = parseInt(cell.getAttribute('data-image-id'), 10);
    if (!file) return true;
    if (flags.hideCollection && file.indexOf('collection') >= 0) return false;
    if (!flags.filterVariant) return true;
    var owner = ownerOf(file, cellId, ctx);
    var curSku = norm(variant.sku);
    if (owner) return owner === curSku;
    var col = imgColorOf(file, ctx);
    var curColor = ctx.ci >= 0 ? norm(variant.options && variant.options[ctx.ci]) : '';
    if (col) return col === curColor;
    return true;
  }

  function cleanHtml(el) {
    var c = el.cloneNode(true);
    c.classList.remove('is-selected', 'is-nav-selected', 'flickity-slider');
    c.style.position = '';
    c.style.left = '';
    c.style.opacity = '';   // flickity-fade bakes opacity inline — clear it so the stored
    c.style.transform = ''; // clone doesn't reappear pre-faded when re-appended.
    c.removeAttribute('aria-hidden');
    c.removeAttribute('tabindex');
    return c.outerHTML;
  }

  function selectFirst(flkty) {
    if (!flkty || !flkty.select) return;
    try { flkty.select(0, false, true); } catch (e) {}
  }

  // The main carousel uses fade:true — flickity-fade shows a cell purely via inline opacity,
  // and after a remove/append it keeps repainting from a STALE selectedIndex (the last cell),
  // which a plain opacity set can't win against. flickity-fade writes `style.opacity` WITHOUT
  // !important, so we lock the first cell visible with an !important opacity that it can't
  // override — then release the lock the instant the shopper navigates to another slide, so
  // the fade behaves normally from there.
  function pinFadeFirst(container, flkty) {
    selectFirst(flkty);
    var cells = container.querySelectorAll('.carousel-cell');
    for (var i = 0; i < cells.length; i++) {
      cells[i].style.setProperty('opacity', i === 0 ? '1' : '0', 'important');
    }
    if (!flkty._dvlUnpin) {
      flkty._dvlUnpin = function () {
        if (flkty.selectedIndex === 0) return; // our own / initial selection — keep the lock
        container.querySelectorAll('.carousel-cell').forEach(function (c) { c.style.removeProperty('opacity'); });
      };
      try { flkty.on('select', flkty._dvlUnpin); } catch (e) {}
    }
  }

  // Flickity caches every cell's measured height. The cells we append are FRESH CLONES, so the
  // browser can hand them a decoded image only AFTER Flickity has measured them — most likely on
  // a warm cache, where the <img> is served from disk a beat behind our synchronous resize(). The
  // selected cell then stays cached at height 0, adaptiveHeight writes `height: 0px` on the
  // viewport, the stylesheet's `#slider .flickity-viewport { min-height: 300px !important }` floors
  // it at 300px, and the viewport's overflow:hidden crops the hero image mid-way with the
  // thumbnail grid drawn straight across it. Nothing re-measures on its own, so do it here once
  // the images have actually laid out.
  function settleSize(container, flkty, isMain) {
    function settle() {
      try { flkty.resize(); } catch (e) {}
      // Only re-pin while we're still on our own initial selection — once the shopper has
      // navigated, pinFadeFirst would yank them back to the first slide.
      if (isMain && flkty.selectedIndex === 0) { try { pinFadeFirst(container, flkty); } catch (e) {} }
    }

    // After the next layout pass, when cache-hit images have their intrinsic box.
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { requestAnimationFrame(settle); });
    }

    // And again as each still-decoding image lands.
    Array.prototype.forEach.call(container.querySelectorAll('img'), function (img) {
      if (img.complete && img.naturalHeight) return;
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });

    // Safety net for lazysizes swapping in a data-src after all of the above.
    setTimeout(settle, 500);
  }

  // The theme marks every media wrapper `.inactive` except the one on show, and its switchMedia()
  // starts with main.querySelector('[data-product-single-media-wrapper]:not(.inactive)').dispatchEvent(…)
  // — no null check. When the one on show was a -collection- composite we just filtered out, every
  // kept wrapper is `.inactive`, that lookup is null, it throws, and the rest of the variant handler
  // (the ?variant= URL update included) never runs. The first kept cell is the one we show: make it
  // the active one.
  function markActiveMedia(container) {
    var wrappers = container.querySelectorAll('[data-product-single-media-wrapper]');
    Array.prototype.forEach.call(wrappers, function (w, i) {
      w.classList.toggle('inactive', i !== 0);
      w.classList.toggle('active-slide', i === 0);
    });
  }

  function filterCarousel(container, ctx, variant, flags) {
    if (!container || !window.Flickity) return;
    var flkty = window.Flickity.data(container);
    if (!flkty) return;

    if (!STORE.has(container)) {
      var cells = Array.prototype.slice.call(container.querySelectorAll('.carousel-cell'));
      STORE.set(container, cells.map(cleanHtml));
    }
    var full = STORE.get(container);
    if (!full.length) return;

    var tmp = document.createElement('div');
    function pick(test) {
      var out = [];
      full.forEach(function (html) {
        tmp.innerHTML = html;
        var cell = tmp.firstElementChild;
        if (test(cell)) out.push(cell.cloneNode(true));
      });
      return out;
    }
    var keepEls = pick(function (c) { return cellVisible(c, ctx, variant, flags); });
    var subs = standIns(ctx, variant);
    for (var si = 0; !keepEls.length && si < subs.length; si++) {
      keepEls = pick(function (c) { return cellVisible(c, ctx, subs[si], flags); });
    }
    // never blank the gallery — but still never show the collection composites
    if (!keepEls.length) keepEls = pick(function (c) { return !(flags.hideCollection && isCollectionFile(c.getAttribute('data-dvl-file'))); });
    if (!keepEls.length) keepEls = pick(function () { return true; });

    try {
      var isMain = container.classList.contains('js-carousel-main');
      var oldCells = Array.prototype.slice.call(container.querySelectorAll('.carousel-cell'));
      // APPEND the kept clones FIRST, select one, THEN remove the originals — so the carousel is
      // never emptied. Removing ALL cells nulls Flickity's selectedElement, and the theme's
      // 'change' handler then crashes on it ("Cannot read properties of null"), which aborted the
      // whole main filter and left Flickity on the last cell.
      flkty.append(keepEls);
      var firstNew = container.querySelectorAll('.carousel-cell')[oldCells.length];
      if (firstNew && flkty.selectCell) { try { flkty.selectCell(firstNew, false, true); } catch (e0) {} }
      flkty.remove(oldCells);
      if (isMain) markActiveMedia(container);
      if (isMain) pinFadeFirst(container, flkty);
      else selectFirst(flkty);
      // Non-critical, isolated so a throw here can't undo the selection above.
      try {
        Array.prototype.forEach.call(container.querySelectorAll('.carousel-cell'), function (c, i) {
          c.setAttribute('data-slide-index', String(i));
        });
        flkty.resize();
        if (isMain) pinFadeFirst(container, flkty); // re-assert after resize repaints the fade
      } catch (e2) {
        if (isMain) pinFadeFirst(container, flkty);
      }
      settleSize(container, flkty, isMain);
    } catch (e) { /* leave as-is on any Flickity hiccup */ }
  }

  function carousels() {
    var out = [];
    ['.js-carousel-main', '.js-thumb-carousel-nav'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) { out.push(el); });
    });
    return out;
  }

  function run() {
    var flags = readFlags();
    if (!flags.on) return;
    var product = getProduct();
    if (!product || !product.variants) return;
    var ctx = buildContext(product);

    function apply() {
      var variant = currentVariant(product);
      if (!variant) return;
      carousels().forEach(function (c) { filterCarousel(c, ctx, variant, flags); });
    }

    // Wait for Flickity to be ready on the main carousel, then filter.
    var tries = 0;
    (function waitInit() {
      var main = document.querySelector('.js-carousel-main');
      if (main && window.Flickity && window.Flickity.data(main)) { apply(); }
      else if (tries++ < 60) { setTimeout(waitInit, 100); }
    })();

    // Re-filter on variant change.
    var lastVid = (function () {
      var i = document.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
      if (i && i.value) return i.value;
      return new URLSearchParams(location.search).get('variant');
    })();
    var t = null;
    function maybe() {
      clearTimeout(t);
      t = setTimeout(function () {
        var i = document.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
        var v = i && i.value ? i.value : null;
        if (!v) v = new URLSearchParams(location.search).get('variant');
        if (v && v !== lastVid) { lastVid = v; apply(); }
      }, 140);
    }
    /* The theme dispatches `variant:changed` on document in the MIDDLE of its variant handler and asks
       Flickity for the new variant's cell at the END of it — synchronously, in the same tick. Our other
       listeners debounce by 140ms, which is four eternities too late: the lookup happens against the
       PREVIOUS variant's cells. Applying here, synchronously, puts the right cells in place first. */
    document.addEventListener('variant:changed', function (e) {
      var v = e && e.detail && e.detail.variant;
      if (!v || !v.id) { maybe(); return; }
      lastVid = String(v.id);
      var known = product.variants.filter(function (x) { return String(x.id) === String(v.id); })[0] || v;
      carousels().forEach(function (c) { filterCarousel(c, ctx, known, flags); });
      steerThemeLookup(v);
    });

    /* Right after this event the theme runs
         el = main.querySelector("[data-image-id='" + variant.featured_media.id + "']"); index = el.dataset.slideIndex;
       and our variants' featured image is usually the -collection- composite, which we've just hidden.
       A null `el` throws and abandons the rest of the theme's handler — including the ?variant= URL
       update. So, for this one lookup only, point the variant at the first image we kept (the one we
       show anyway), and put its real featured_media back once the theme's handler has finished. */
    function steerThemeLookup(v) {
      var main = document.querySelector('.js-carousel-main');
      if (!main || !v.featured_media || !v.featured_media.id) return;
      if (main.querySelector("[data-image-id='" + v.featured_media.id + "']")) return;
      var first = main.querySelector('.carousel-cell[data-image-id]');
      if (!first) return;
      var real = v.featured_media;
      v.featured_media = { id: parseInt(first.getAttribute('data-image-id'), 10) };
      setTimeout(function () { v.featured_media = real; }, 0);
    }
    document.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('select[name="id"], input[name="id"], .single-option-selector, [data-single-option-selector]')) maybe();
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.swatch-element, .color-swatch, .variant-swatch, [data-swatch], .swatch label')) maybe();
    });
    window.addEventListener('popstate', maybe);
    ['variant:changed', 'variantChange', 'theme:variant:change'].forEach(function (ev) { document.addEventListener(ev, maybe); });
    var _ps = history.pushState, _rs = history.replaceState;
    history.pushState = function () { _ps.apply(this, arguments); setTimeout(maybe, 10); };
    history.replaceState = function () { _rs.apply(this, arguments); setTimeout(maybe, 10); };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
